import React, { useState, useEffect, useContext } from 'react';
import { FaCheck, FaTimes, FaCrown } from 'react-icons/fa';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import './SubscriptionPlan.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SubscriptionPlan = ({ onUpgrade }) => {
  const { user } = useContext(AuthContext);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  const fetchSubscription = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/payment/premium/initiate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // PayHere JavaScript SDK approach - create form and submit
      // This follows PayHere's recommended method from their documentation
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = response.data.paymentUrl;
      
      // Add all PayHere parameters in the correct order
      const params = response.data.params;
      const paramOrder = [
        'merchant_id',
        'return_url',
        'cancel_url',
        'notify_url',
        'first_name',
        'last_name',
        'email',
        'phone',
        'address',
        'city',
        'country',
        'order_id',
        'items',
        'currency',
        'amount',
        'recurrence',
        'duration',
        'hash'
      ];
      
      // Add parameters in PayHere's expected order
      paramOrder.forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = params[key];
          form.appendChild(input);
        }
      });
      
      // Also add any additional parameters that might exist
      Object.keys(params).forEach(key => {
        if (!paramOrder.includes(key) && params[key] !== undefined && params[key] !== null && params[key] !== '') {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = params[key];
          form.appendChild(input);
        }
      });
      
      // Store order_id in localStorage before redirecting
      // PayHere doesn't pass parameters to return_url, so we need to store it
      if (params.order_id) {
        localStorage.setItem('payhere_order_id', params.order_id);
      }
      
      document.body.appendChild(form);
      form.submit();
      
    } catch (error) {
      console.error('Error initiating payment:', error);
      alert(error.response?.data?.error || 'Failed to initiate payment');
      setUpgrading(false);
    }
  };

  if (loading) {
    return <div className="subscription-loading">Loading subscription info...</div>;
  }

  const isPremium = subscription?.isPremium || false;
  const plan = subscription?.plan || 'free';

  // Compact view for premium users
  if (isPremium) {
    return (
      <div className="subscription-plan subscription-plan-compact">
        <div className="plan-card-compact premium">
          <div className="plan-header-compact">
            <FaCrown className="crown-icon-small" />
            <span className="plan-name-compact">Premium Plan</span>
            <span className="premium-badge-small">Active</span>
            {subscription?.expiresAt && (
              <span className="plan-expiry-compact">
                Expires: {new Date(subscription.expiresAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Full card for free users (to encourage upgrade)
  return (
    <div className="subscription-plan">
      <div className="plan-card free">
        <div className="plan-header">
          <h3>Free Plan</h3>
        </div>

        <div className="plan-features">
          <div className="feature-item">
            <FaCheck className="check-icon" />
            <span>vCards: {subscription?.features?.maxVCards || 3}</span>
          </div>
          <div className="feature-item">
            <FaCheck className="check-icon" />
            <span>Services: {subscription?.features?.maxServices || 2}</span>
          </div>
          <div className="feature-item">
            <FaTimes className="times-icon" />
            <span>Brochures/Flipbook: Not Available</span>
          </div>
          <div className="feature-item">
            <FaTimes className="times-icon" />
            <span>Ecommerce Products: Not Available</span>
          </div>
          <div className="feature-item">
            <FaCheck className="check-icon" />
            <span>File Upload: {subscription?.features?.maxFileSize || 5}MB max</span>
          </div>
        </div>

        <div className="plan-actions">
          <button
            onClick={handleUpgrade}
            disabled={upgrading}
            className="btn btn-primary btn-upgrade"
          >
            {upgrading ? 'Redirecting to Payment...' : 'Upgrade to Premium'}
          </button>
          <p className="upgrade-note">Secure payment via PayHere</p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlan;


